// model
import {IInsurance} from "@/model/Insurance/Insurance"
import {IInsuranceFamily} from "@/model/InsuranceFamily/InsuranceFamily"
import ParticipantInsuranceFamily from "@/model/InsuranceFamily/ParticipantInsuranceFamily"
import InsuranceFamilyFactory from "@/model/InsuranceFamily/InsuranceFamilyFactory"
import InsuranceFactory from "@/model/Insurance/InsuranceFactory"

import { ITraveller } from "@/model/Traveller"
import InsuranceFamilyContent, {IInsuranceFamilyContent} from "@/model/Content/InsuranceFamilyContent"
import BookingData from "@/classes/BookingData"
import ParticipantInsurance from "@/model/Insurance/ParticipantInsurance"
import GlobalInsurance from "@/model/Insurance/GlobalInsurance"
import GlobalInsuranceFamily from "@/model/InsuranceFamily/GlobalInsuranceFamily"

import { InsuranceQuote, QuoteState } from "@requests"

import * as _ from "lodash"


const processInsurances = (bookingData: BookingData, sourceObj: QuoteState) => {
    // create insurance and insurance family objects, and interconnect with eachother and accomodations and travellers if needed
    const insurancesData = _.cloneDeep(sourceObj.insurances)
    const insurancesArr = Object.entries(insurancesData) // crazy data shapes returned by API but ok
    insurancesArr.forEach(([insuranceFamilyId, quoteObjArr]) => {
        // create insurance family content object in the beginning to get the type (participant vs accomodation vs global)
        const split = quoteObjArr[0].id.split("_")
        const idOfFirstInsurance = split[split.length - 1]
        const serviceContentResponse = bookingData.responses.serviceContentResponses[idOfFirstInsurance]
        const insuranceFamilyContent: IInsuranceFamilyContent = new InsuranceFamilyContent(serviceContentResponse?.name)

        // create insurance family
        const insuranceFamily: IInsuranceFamily = InsuranceFamilyFactory.createInsuranceFamily(insuranceFamilyId);


        // set content for insurance family
        insuranceFamily.setInsuranceFamilyContent(insuranceFamilyContent);
        insuranceFamilyContent.setInsuranceFamily(insuranceFamily);

        // create insurance objects and interconnect
        (quoteObjArr as InsuranceQuote[]).forEach((quoteObj: InsuranceQuote) => {
            // create insurance
            if (!quoteObj) throw new Error("quoteObj is mandatory")
            const split = quoteObj.id.split("_")
            const id = split[split.length - 1]
            const responseContentObj = bookingData.responses.serviceContentResponses[id]
            const insurance: IInsurance = InsuranceFactory.createInsurance(quoteObj, responseContentObj, insuranceFamily)

            // connect travellers to participant type services and families
            if (insurance instanceof ParticipantInsurance && insuranceFamily instanceof ParticipantInsuranceFamily) {
                const eligibleTravellersIdsSet: Set<string> = new Set()
                const selectedTravellersIdsSet: Set<string> = new Set()
                quoteObj.travellers?.forEach((travellerQuoteObj) => {
                    eligibleTravellersIdsSet.add(travellerQuoteObj.id)
                    if (travellerQuoteObj.selected) selectedTravellersIdsSet.add(travellerQuoteObj.id)
                })
                
                bookingData.travellersArr.forEach((traveller: ITraveller) => {
                    if (eligibleTravellersIdsSet.has(traveller.getId())) {
                        insurance.addTravellerEligible(traveller)
                        traveller.addInsuranceEligible(insurance)
                        insuranceFamily.addTravellerEligible(traveller)
                    }
                    if (selectedTravellersIdsSet.has(traveller.getId())) {
                        insurance.addTravellerSelected(traveller)
                        traveller.addInsuranceSelected(insurance)
                        traveller.addInsuranceFamilySelected(insuranceFamily)
                        insuranceFamily.addTravellerSelected(traveller)
                    }
                })
            }
            // attach global insurance to lihe
            else if (insurance instanceof GlobalInsurance && insuranceFamily instanceof GlobalInsuranceFamily) {
                bookingData.lihe.addGlobalInsurance(insurance)
                if (insurance.getSelected()) bookingData.lihe.addGlobalInsuranceSelected(insurance)
            }

            // interconnect insurance and insurance family
            insurance.setInsuranceFamily(insuranceFamily)
            insuranceFamily.addInsurance(insurance)
            if (insurance instanceof ParticipantInsurance) {
                const travellers = bookingData.travellersArr
                travellers.forEach((traveller: ITraveller) => {
                    if (insurance.getIsSelectedForTravellerId(traveller.getId())) {
                        insuranceFamily.addInsuranceSelected(insurance)
                        bookingData.selectedInsurancesMap.set(insurance.getIdFull(), insurance)
                        bookingData.selectedInsuranceFamiliesMap.set(insuranceFamilyId, insuranceFamily)
                    }
                    if (insurance.getIsIncludedForTravellerId(traveller.getId())) {
                        insuranceFamily.addInsuranceIncluded(insurance)
                    }
                })
            } else if (insurance instanceof GlobalInsurance) {
                if (insurance.getSelected()) {
                    insuranceFamily.addInsuranceSelected(insurance)
                    bookingData.selectedInsurancesMap.set(insurance.getIdFull(), insurance)
                    bookingData.selectedInsuranceFamiliesMap.set(insuranceFamilyId, insuranceFamily)
                }
                if (insurance.getIncluded()) insuranceFamily.addInsuranceIncluded(insurance)
            }

            // save insurance
            if (bookingData.insurancesMap.get(insurance.getIdFull())) throw new Error(`insurance for this id was already created: ${insurance.getIdFull()}`)
            bookingData.insurancesArr.push(insurance)
            bookingData.insurancesMap.set(insurance.getIdFull(), insurance)
        })

        // save insurance family
        if (bookingData.insuranceFamiliesMap.get(insuranceFamilyId)) throw new Error(`insuranceFamily for this id was already created: ${insuranceFamilyId}`)
        bookingData.insuranceFamiliesArr.push(insuranceFamily)
        bookingData.insuranceFamiliesMap.set(insuranceFamilyId, insuranceFamily)
    })

}

export default processInsurances